import { useEffect, useRef, useState } from "react";

const HERO_POSTER_SRC = "/images/hero/hero.png";
const HERO_VIDEO_DESKTOP_SRC = "/videos/hero-desktop.webm";
const HERO_VIDEO_MOBILE_SRC = "/videos/hero-mobile.webm";
const IDLE_TIMEOUT_MS = 2500;
const IDLE_FALLBACK_MS = 1200;

type NetworkConnection = {
  saveData?: boolean;
  effectiveType?: string;
  downlink?: number;
};

function getNetworkConnection(): NetworkConnection | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkConnection;
    mozConnection?: NetworkConnection;
    webkitConnection?: NetworkConnection;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

function shouldLoadHeroVideo(): boolean {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return false;
  }

  const connection = getNetworkConnection();
  if (!connection) return true;
  if (connection.saveData) return false;

  const effectiveType = connection.effectiveType;
  if (effectiveType === "slow-2g" || effectiveType === "2g") return false;

  if (typeof connection.downlink === "number" && connection.downlink > 0 && connection.downlink < 0.5) {
    return false;
  }

  return true;
}

function resolveHeroVideoSrc(): string {
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  return isMobile ? HERO_VIDEO_MOBILE_SRC : HERO_VIDEO_DESKTOP_SRC;
}

function scheduleIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
    return () => window.cancelIdleCallback(id);
  }

  const id = window.setTimeout(callback, IDLE_FALLBACK_MS);
  return () => window.clearTimeout(id);
}

export default function HeroDemoVideo() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cancelIdle: (() => void) | undefined;

    const startDeferredLoad = () => {
      if (cancelled || !shouldLoadHeroVideo()) return;
      cancelIdle = scheduleIdle(() => {
        if (cancelled) return;
        setVideoSrc(resolveHeroVideoSrc());
      });
    };

    if (document.readyState === "complete") {
      startDeferredLoad();
    } else {
      window.addEventListener("load", startDeferredLoad, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", startDeferredLoad);
      cancelIdle?.();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    video.preload = "auto";
    void video.play().catch(() => {
      /* Autoplay can still be blocked; poster remains visible. */
    });
  }, [videoSrc]);

  return (
    <div className="relative w-full pt-[56.25%]">
      <img
        alt={isPlaying ? "" : "3Chat 产品演示"}
        aria-hidden={isPlaying ? true : undefined}
        className="absolute inset-0 h-full w-full bg-black object-contain"
        decoding="async"
        fetchPriority="high"
        src={HERO_POSTER_SRC}
      />
      {videoSrc ? (
        <video
          ref={videoRef}
          autoPlay
          controls
          loop
          muted
          playsInline
          preload="auto"
          className={`absolute inset-0 h-full w-full bg-black object-contain transition-opacity duration-300 ${
            isPlaying ? "opacity-100" : "opacity-0"
          }`}
          poster={HERO_POSTER_SRC}
          src={videoSrc}
          title="3Chat 产品演示"
          onPlaying={() => setIsPlaying(true)}
        />
      ) : null}
    </div>
  );
}
